<script setup lang="ts">
    import { ILibraryWidget } from '@/js/services/api/library/types';
    import { ref, watch } from 'vue';
    import WidgetLibraryItem from './WidgetLibraryItem.vue';
    import WidgetCategoryPicker from './WidgetCategoryPicker.vue';
    import { useWidgetLibraryStore } from '@/js/stores/widgetLibrary';
    import EchoButton from '../EchoButton.vue';
    import { __ } from '@/js/stores/expressions';

    import Modal, { ModalSize } from '../Modal.vue';
    import SelectDashboardForm from '../SelectDashboardForm.vue';
    import CreateWidgetModal from '../widgets/CreateWidgetModal.vue';
    import { useLibraryRouteSync } from '@/js/composables/useLibraryRouteSync';

    interface Props {
        widgets: ILibraryWidget[];
        searchString: string;
    }

    const props = defineProps<Props>();

    const widgetLibrary = useWidgetLibraryStore();

    const { widgetFormOpen, setWidgetFormParams, closeWidgetForm, goToDashboard } =
        useLibraryRouteSync();

    const showDashboardForm = ref(false);

    watch(
        () => props.widgets.length,
        () => widgetLibrary.setInitialWigets(props.widgets),
        { immediate: true }
    );

    watch(
        () => props.searchString,
        (searchString) => {
            widgetLibrary.filterWidgets(searchString);
        }
    );

    const handleCustomizeWidget = (dashboardId: string) => {
        if (!widgetLibrary.selectedWidget) {
            return;
        }
        showDashboardForm.value = false;
        setWidgetFormParams(dashboardId, widgetLibrary.selectedWidget);
    };

    const handleCustomizeWidgetSubmit = (dashboardId?: string) => {
        if (dashboardId) {
            goToDashboard(dashboardId);
            widgetLibrary.selectedWidget = '';
        } else {
            closeWidgetForm();
        }
    };
</script>
<template>
    <Modal :show="showDashboardForm" :size="ModalSize.Medium" @close="showDashboardForm = false">
        <SelectDashboardForm @submit="handleCustomizeWidget" />
    </Modal>
    <CreateWidgetModal :show="widgetFormOpen" @close="handleCustomizeWidgetSubmit" />
    <WidgetCategoryPicker
        :categories="widgetLibrary.categoryFilter"
        @select="widgetLibrary.updateCategoryFilter"
    />
    <div class="h-full flex flex-col gap-3">
        <div v-for="widgetCategory in widgetLibrary.widgetGrid" :key="widgetCategory.category">
            <div v-if="widgetLibrary.categoryFilter[widgetCategory.category]" class="mt-15">
                <h1 class="text-2xl">
                    {{ widgetCategory.category }}
                </h1>
                <div
                    class="mt-4 mb-10 grid grid-cols-[1fr] lg:grid-cols-[1fr_1fr] xl:grid-cols-[1fr_1fr_1fr] gap-3"
                >
                    <WidgetLibraryItem
                        v-for="widget in widgetCategory.widgets"
                        :key="widget.id"
                        :widget="widget"
                        :selected="widget.id === widgetLibrary.selectedWidget"
                        @select="widgetLibrary.handleSelect"
                    />
                </div>
            </div>
        </div>
    </div>
    <div
        class="w-full fixed bottom-20 left-0 flex justify-center items-center h-0 overflow-visible"
    >
        <EchoButton v-if="widgetLibrary.selectedWidget" @click="showDashboardForm = true">
            {{ __('library-customize-widget') }}
        </EchoButton>
    </div>
</template>
